/**
 * Shared peek carousel (Flickity): fractional visible slides on mobile/tablet,
 * optional static grid on desktop. Configure via <peek-carousel data-*> or
 * PeekCarousel.Controller for custom sections (e.g. product tabs).
 */
(function () {
  const DEFAULT_DESKTOP_QUERY = "(min-width: 1068px)";
  const DEFAULT_TABLET_QUERY = "(min-width: 768px) and (max-width: 1067px)";

  function flickityAvailable() {
    return typeof window.Flickity !== "undefined";
  }

  function debounce(fn, delay) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(null, args), delay);
    };
  }

  function parseBool(value, defaultValue) {
    if (value == null || value === "") return defaultValue;
    return value === "true" || value === "1";
  }

  function parseNumber(value, fallback) {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : fallback;
  }

  class PeekCarouselController {
    constructor(host, config) {
      this.host = host;
      this.config = {
        desktopQuery: DEFAULT_DESKTOP_QUERY,
        tabletQuery: DEFAULT_TABLET_QUERY,
        mobileVisible: 1.5,
        tabletVisible: 2.5,
        desktopVisible: null,
        gapScope: null,
        gapVar: null,
        gapDefault: 8,
        sliderSelector: ".products.carousel",
        slideSelector: null,
        staticClass: "",
        carouselClass: "",
        flickityCellSelector: ".carousel__slide",
        clickSelector: "",
        wrapAround: false,
        syncHeight: true,
        adaptiveHeight: false,
        desktopStatic: true,
        mobileStatic: false,
        reuseFlickity: false,
        removeSlideClassOnStatic: false,
        autoMarkSlides: true,
        getSlider: null,
        onBeforeStatic: null,
        onBeforeCarousel: null,
        onAfterCarousel: null,
        ...config,
      };
      this.slider = null;
      this.onResize = null;
      this.onLoad = null;
      this._pollTimer = null;
    }

    static configFromElement(el) {
      return {
        desktopQuery: el.dataset.desktopQuery || DEFAULT_DESKTOP_QUERY,
        tabletQuery: el.dataset.tabletQuery || DEFAULT_TABLET_QUERY,
        mobileVisible: parseNumber(el.dataset.mobileVisible, 1.5),
        tabletVisible: parseNumber(el.dataset.tabletVisible, 2.5),
        desktopVisible:
          el.dataset.desktopVisible != null && el.dataset.desktopVisible !== ""
            ? parseNumber(el.dataset.desktopVisible, 4.5)
            : null,
        gapScope: el.dataset.gapScope || null,
        gapVar: el.dataset.gapVar || null,
        gapDefault: parseNumber(el.dataset.gapDefault, 8),
        sliderSelector: el.dataset.sliderSelector || ".products.carousel",
        slideSelector: el.dataset.slideSelector || null,
        staticClass: el.dataset.staticClass || "",
        carouselClass: el.dataset.carouselClass || "",
        flickityCellSelector: el.dataset.flickityCellSelector || ".carousel__slide",
        clickSelector: el.dataset.clickSelector || "",
        wrapAround: parseBool(el.dataset.wrapAround, false),
        syncHeight: parseBool(el.dataset.syncHeight, true),
        adaptiveHeight: parseBool(el.dataset.adaptiveHeight, false),
        desktopStatic: parseBool(el.dataset.desktopStatic, true),
        mobileStatic: parseBool(el.dataset.mobileStatic, false),
        reuseFlickity: parseBool(el.dataset.reuseFlickity, false),
        removeSlideClassOnStatic: parseBool(el.dataset.removeSlideClassOnStatic, false),
        autoMarkSlides: parseBool(el.dataset.autoMarkSlides, true),
      };
    }

    resolveSlider() {
      if (typeof this.config.getSlider === "function") {
        return this.config.getSlider();
      }
      return this.host.querySelector(this.config.sliderSelector);
    }

    getLayoutHost() {
      if (this.host.tagName === "PEEK-CAROUSEL") return this.host;

      const carouselWrap = this.slider?.closest(
        ".product-tab__carousel, .product-list__carousel, .product-banner__carousel, .collection-list-cards__carousel"
      );

      return carouselWrap || this.slider?.parentElement || this.host;
    }

    applyHostLayoutVars() {
      const host = this.getLayoutHost();
      if (!host) return;

      host.style.setProperty("--peek-visible", String(this.getVisibleCount()));
      host.style.setProperty("--peek-gap", `${this.getGap()}px`);
    }

    beginPendingLayout() {
      const host = this.getLayoutHost();

      host?.classList.add("is-loading", "peek-pending-layout-host");

      if (this.host !== host) {
        this.host.classList.add("is-loading");
      }

      if (!this.slider) return;

      this.slider.classList.add("peek-pending-layout");
      this.applyHostLayoutVars();

      if (this.config.autoMarkSlides) {
        this.markCarouselSlides();
      }

      this.reserveSliderMinHeight();
    }

    reserveSliderMinHeight() {
      if (!this.slider || !this.config.syncHeight) return;
      if (this.config.desktopStatic && this.isDesktop()) return;
      if (this.config.mobileStatic && this.isMobileStatic()) return;

      const cell =
        this.slider.querySelector(".carousel__slide") || this.getSlideElements()[0];
      if (!cell) return;

      const height = this.getSlideContentHeight(cell);
      if (height > 0) {
        this.slider.style.minHeight = `${height}px`;
      }
    }

    finishPendingLayout() {
      const host = this.getLayoutHost();

      host?.classList.remove("is-loading");
      host?.classList.add("is-ready");

      if (this.host !== host) {
        this.host.classList.remove("is-loading");
        this.host.classList.add("is-ready");
      }

      if (!this.slider) return;

      this.slider.classList.remove("peek-pending-layout", "is-loading");
      this.slider.classList.add("is-ready");
      this.slider.style.removeProperty("min-height");
    }

    init() {
      this.slider = this.resolveSlider();
      if (!this.slider) return;

      this.beginPendingLayout();

      this.onResize = debounce(() => this.updateLayout(), 150);
      this.onLoad = () => this.updateLayout();

      this.slider.addEventListener("ready.flickity", () => {
        const flkty = Flickity.data(this.slider);
        if (flkty && this.config.syncHeight) this.syncViewportHeight(flkty);
        this.finishPendingLayout();
      });

      window.addEventListener("load", this.onLoad);
      window.addEventListener("resize", this.onResize);

      this.updateLayout();

      let attempts = 0;
      this._pollTimer = setInterval(() => {
        if (Flickity.data(this.slider) || ++attempts > 40) {
          clearInterval(this._pollTimer);
          this._pollTimer = null;
          this.updateLayout();
        }
      }, 100);
    }

    destroy() {
      if (this._pollTimer) clearInterval(this._pollTimer);
      window.removeEventListener("resize", this.onResize);
      window.removeEventListener("load", this.onLoad);
      this.enableStaticGrid();
      this.finishPendingLayout();
    }

    isDesktop() {
      return window.matchMedia(this.config.desktopQuery).matches;
    }

    isTablet() {
      return window.matchMedia(this.config.tabletQuery).matches;
    }

    getVisibleCount() {
      if (this.isDesktop() && this.config.desktopVisible != null) {
        return this.config.desktopVisible;
      }
      return this.isTablet() ? this.config.tabletVisible : this.config.mobileVisible;
    }

    isMobileStatic() {
      return (
        this.config.mobileStatic &&
        window.matchMedia("(max-width: 767px)").matches
      );
    }

    getGap() {
      const scope =
        (this.config.gapScope && this.host.closest(this.config.gapScope)) ||
        this.host.closest("[class*='section-']") ||
        this.host;

      if (this.config.gapVar && scope) {
        const gap = parseFloat(getComputedStyle(scope).getPropertyValue(this.config.gapVar));
        if (Number.isFinite(gap)) return gap;
      }

      return this.config.gapDefault;
    }

    getSlideElements() {
      if (!this.config.slideSelector || !this.slider) return [];
      return Array.from(this.slider.querySelectorAll(this.config.slideSelector));
    }

    clearSlideStyles() {
      this.getSlideElements().forEach((item) => {
        item.style.removeProperty("width");
        item.style.removeProperty("max-width");
        item.style.removeProperty("margin-right");
      });
    }

    getFlickityOptions() {
      return {
        wrapAround: this.config.wrapAround,
        cellAlign: "left",
        pageDots: false,
        contain: true,
        prevNextButtons: false,
        cellSelector: this.config.flickityCellSelector,
        adaptiveHeight: this.config.adaptiveHeight,
        draggable: true,
      };
    }

    getFlickity() {
      if (!flickityAvailable() || !this.slider) return null;
      return Flickity.data(this.slider);
    }

    getSlideContentHeight(cell) {
      if (!cell) return 0;

      const content =
        cell.querySelector("product-card") ||
        cell.querySelector(".product-card") ||
        cell.firstElementChild;

      if (!content) return Math.ceil(cell.getBoundingClientRect().height);

      const saved = {
        cellMinHeight: cell.style.minHeight,
        cellHeight: cell.style.height,
        contentHeight: content instanceof HTMLElement ? content.style.height : "",
        contentMinHeight: content instanceof HTMLElement ? content.style.minHeight : "",
      };

      cell.style.minHeight = "0";
      cell.style.height = "auto";

      if (content instanceof HTMLElement) {
        content.style.height = "auto";
        content.style.minHeight = "0";
      }

      const height = Math.ceil(content.getBoundingClientRect().height);

      cell.style.minHeight = saved.cellMinHeight;
      cell.style.height = saved.cellHeight;

      if (content instanceof HTMLElement) {
        content.style.height = saved.contentHeight;
        content.style.minHeight = saved.contentMinHeight;
      }

      return height;
    }

    syncViewportHeight(flkty) {
      if (!flkty?.viewport) return;

      const cell = flkty.selectedElement || flkty.cells[0]?.element;
      if (!cell) return;

      requestAnimationFrame(() => {
        const height = this.getSlideContentHeight(cell);
        if (height > 0) {
          flkty.viewport.style.height = `${height}px`;
        }
      });
    }

    syncFlickityOptions(flkty) {
      if (!flkty) return;
      flkty.options.wrapAround = this.config.wrapAround;
      flkty.updateDraggable();
    }

    applySlideWidths(flkty) {
      if (!flkty || (this.config.desktopStatic && this.isDesktop())) return;

      const viewportWidth = flkty.size.innerWidth || flkty.element.getBoundingClientRect().width;
      const visibleCount = this.getVisibleCount();
      const gap = this.getGap();
      const cellWidth = Math.max((viewportWidth - gap * 0.5) / visibleCount, 0);

      flkty.cells.forEach((cell) => {
        cell.element.style.width = `${cellWidth}px`;
        cell.element.style.maxWidth = `${cellWidth}px`;
        cell.element.style.marginRight = `${gap}px`;
      });

      flkty.reposition();
      flkty.resize();
      this.syncFlickityOptions(flkty);
      if (this.config.syncHeight) this.syncViewportHeight(flkty);
    }

    markCarouselSlides() {
      this.getSlideElements().forEach((item) => {
        item.classList.add("carousel__slide");
      });
    }

    unmarkCarouselSlides() {
      if (!this.config.removeSlideClassOnStatic) return;
      this.getSlideElements().forEach((item) => {
        item.classList.remove("carousel__slide");
      });
    }

    enableStaticGrid() {
      if (!this.slider) return;
      const flkty = flickityAvailable() ? Flickity.data(this.slider) : null;
      if (flkty) {
        if (flkty.viewport) flkty.viewport.style.removeProperty("height");
        flkty.destroy();
      }

      delete this.slider.dataset.peekLinkClickBound;
      this.config.onBeforeStatic?.(this.slider);

      this.unmarkCarouselSlides();
      this.clearSlideStyles();

      if (this.config.staticClass) {
        this.slider.classList.add(this.config.staticClass);
      }
      if (this.config.carouselClass) {
        this.slider.classList.remove(this.config.carouselClass);
      }

      this.finishPendingLayout();
    }

    bindStaticClick(flkty) {
      if (!flkty || !this.config.clickSelector || this.slider.dataset.peekLinkClickBound === "true") {
        return;
      }

      flkty.on("staticClick", (event, _pointer, cellElement) => {
        if (
          event.target?.closest(
            "button, input, select, textarea, label, .product-tab-card__image-nav, [data-peek-carousel-ignore]"
          )
        ) {
          return;
        }

        const link =
          event.target?.closest(this.config.clickSelector) ||
          cellElement?.querySelector(this.config.clickSelector);
        const href = link?.getAttribute("href");
        if (!href || href === "#") return;
        window.location.assign(href);
      });

      this.slider.dataset.peekLinkClickBound = "true";
    }

    enableCarousel() {
      if (!flickityAvailable()) return null;

      this.config.onBeforeCarousel?.(this.slider);

      if (this.config.staticClass) {
        this.slider.classList.remove(this.config.staticClass);
      }
      if (this.config.carouselClass) {
        this.slider.classList.add(this.config.carouselClass);
      }

      if (this.config.autoMarkSlides) {
        this.markCarouselSlides();
      }

      let flkty = Flickity.data(this.slider);

      if (this.config.reuseFlickity && flkty) {
        flkty.options.draggable = true;
        flkty.options.wrapAround = this.config.wrapAround;
        flkty.updateDraggable();
        flkty.reloadCells();
        flkty.select(0, false, true);
      } else {
        if (flkty) flkty.destroy();
        delete this.slider.dataset.peekLinkClickBound;
        flkty = new Flickity(this.slider, this.getFlickityOptions());
      }

      this.bindStaticClick(flkty);

      if (this.config.syncHeight) {
        flkty.on("select", () => this.syncViewportHeight(flkty));
        flkty.on("ready", () => this.syncViewportHeight(flkty));
      }

      this.applySlideWidths(flkty);

      requestAnimationFrame(() => {
        if (this.config.syncHeight) this.syncViewportHeight(flkty);
        this.finishPendingLayout();
      });

      this.config.onAfterCarousel?.(this.slider, flkty);
      return flkty;
    }

    updateLayout() {
      this.slider = this.resolveSlider();
      if (!this.slider) return;

      this.applyHostLayoutVars();

      if (this.config.mobileStatic && this.isMobileStatic()) {
        this.enableStaticGrid();
        return;
      }

      if (this.config.desktopStatic && this.isDesktop()) {
        this.enableStaticGrid();
        return;
      }

      if (!flickityAvailable()) {
        this.beginPendingLayout();
        return;
      }

      const flkty = Flickity.data(this.slider);
      if (flkty && this.config.carouselClass && this.slider.classList.contains(this.config.carouselClass)) {
        this.applySlideWidths(flkty);
        this.finishPendingLayout();
        return;
      }

      this.enableCarousel();
    }
  }

  class PeekCarouselElement extends HTMLElement {
    connectedCallback() {
      this.classList.add("is-loading");
      this.controller = new PeekCarouselController(this, PeekCarouselController.configFromElement(this));
      this.controller.init();
    }

    disconnectedCallback() {
      this.controller?.destroy();
      this.controller = null;
    }

    updateLayout() {
      this.controller?.updateLayout();
    }
  }

  window.PeekCarousel = { Controller: PeekCarouselController };

  if (!customElements.get("peek-carousel")) {
    customElements.define("peek-carousel", PeekCarouselElement);
  }
})();
